import { useMutation } from "react-query";

import axios, { AxiosResponse } from "axios";
import { ExportFormat } from "utils/exportUtils";

import { ExportChartDataResponseSchema } from "./useExportChartDataMutation";
import {
  ProductionVolumesFilter,
  toProductionVolumesBody
} from "./useProductionVolumesQuery";

const chartServiceApiRoot = process.env.REACT_APP_CHART_SERVICE;

export interface ExportProductionVolumesParams {
  request: ProductionVolumesFilter;
  format: ExportFormat;
  filename: string;
}

const readBlobText = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

const FAILURE_MESSAGE = "Failed to export monthly production";

// responseType "blob" hides the service's text 400 (for example the format's well limit)
// inside a Blob, so the message is unwrapped for the user. Anything else is not
// written for users, so it gets a generic message.
const toUserError = async (error: unknown) => {
  if (
    axios.isAxiosError(error) &&
    error.response?.status === 400 &&
    error.response.data instanceof Blob
  ) {
    const message = (await readBlobText(error.response.data)).trim();
    if (message) return new Error(message);
  }
  return new Error(FAILURE_MESSAGE);
};

export default function useExportProductionVolumesMutation(
  onSuccess?: (response: AxiosResponse, params: ExportProductionVolumesParams) => void,
  onError?: (error: Error) => void
) {
  return useMutation(
    async ({ request, format }: ExportProductionVolumesParams) => {
      try {
        const response = await axios.post(
          `${chartServiceApiRoot}/productionVolumes/export?exportFormat=${format}`,
          toProductionVolumesBody(request),
          { responseType: "blob" }
        );
        const validatedResponse = ExportChartDataResponseSchema.parse(response);
        return validatedResponse as AxiosResponse<Blob>;
      } catch (error) {
        throw await toUserError(error);
      }
    },
    {
      onSuccess: onSuccess
        ? (response, params) => onSuccess(response, params)
        : undefined,
      onError: onError ? (error) => onError(error as Error) : undefined
    }
  );
}
